import { CtaStrip, FeatureList, MarketingPage, Pillars } from '../../components/MarketingPage';

/**
 * The four LIVE niche landing pages for Phase 1 (per business/strategy/NICHE_STRATEGY.md).
 *
 * These target high-intent keyword searches ("estate auction software",
 * "storage auction software") and inherit the platform pitch with niche-
 * specific vocabulary + pain points.
 */

export function EstateAuctionSoftware() {
  return (
    <MarketingPage
      seoTitle="Estate auction software — AuctiSync"
      seoDescription="Purpose-built estate auction software with consignor statements, persistent inventory, staggered close, and mobile cataloging. Replace MaxSold with something modern."
      canonicalPath="/estate-auction-software"
      eyebrow="Niche"
      headline="Estate auctions, in a modern interface."
      subhead="From sale-bill to staggered close, from consignor commissions to buyer pickup — the full estate auction workflow, without the legacy UI tax."
    >
      <Pillars
        title="Estate auctioneer workflow, end to end"
        items={[
          {
            title: 'Sale-bill + preview setup',
            body: 'Preview windows, preview photos, preview videos, sale-bill PDF — everything buyers look for before a live auction.',
          },
          {
            title: 'Consignor statements',
            body: 'Commission per item, expense tracking, monthly statements, Stripe Connect payouts direct to the consignor.',
          },
          {
            title: 'Staggered close',
            body: 'Configurable lots-per-group and minutes-between-groups. Keeps your buyers engaged across a weekly sale.',
          },
          {
            title: 'Print catalog',
            body: 'For the buyers who still read on paper. PDF-ready, branded, printable by lot-number range.',
          },
          {
            title: 'Mobile cataloging app',
            body: 'Walk the estate, snap the items, generate lot numbers in minutes. Staff app, iOS + Android.',
          },
          {
            title: 'Pickup + after-sale',
            body: 'Pickup windows, invoice consolidation across multiple auctions, after-sale reporting.',
          },
        ]}
      />

      <CtaStrip
        headline="Run your next estate auction on AuctiSync."
        primaryLabel="See pricing"
        primaryHref="/pricing"
        secondaryLabel="DTA case study"
        secondaryHref="/case-studies/dta"
      />
    </MarketingPage>
  );
}

export function AntiqueAuctionSoftware() {
  return (
    <MarketingPage
      seoTitle="Antique auction software — AuctiSync"
      seoDescription="Antique and vintage auction software with provenance attachments, condition reports, COA support, and the kind of detail-rich catalog your buyers expect."
      canonicalPath="/antique-auction-software"
      eyebrow="Niche"
      headline="Antique auctions, documented down to the provenance."
      subhead="Condition reports, provenance attachments, COAs, high-resolution photography. The kind of cataloging discipline an antique buyer reads — and trusts."
    >
      <Pillars
        title="What antique buyers look for"
        items={[
          {
            title: 'Condition reports',
            body: 'Structured condition fields, not just a note. Buyers filter + sort.',
          },
          {
            title: 'Provenance tracking',
            body: 'Attach ownership history, exhibition records, prior sale prices. Shown on the lot page.',
          },
          {
            title: 'COA + appraisal attachments',
            body: 'Upload and show certificates of authenticity + appraisals. PDF or image.',
          },
          {
            title: 'High-res photography',
            body: 'Cloudflare R2 CDN for zero-egress-cost hosting. 20+ photos per lot, no storage panic.',
          },
          {
            title: 'Reserve + stretch bidding',
            body: 'Reserves shown to bidders or hidden. Reserve-gauge UX tells buyers how close they are.',
          },
          {
            title: 'Consignor-friendly fees',
            body: 'Per-item commissions. Expense scales. No hidden platform fees.',
          },
        ]}
      />

      <CtaStrip
        headline="Catalog your next antique auction the way buyers want to read it."
        primaryLabel="See pricing"
        primaryHref="/pricing"
      />
    </MarketingPage>
  );
}

export function CollectiblesAuctionSoftware() {
  return (
    <MarketingPage
      seoTitle="Collectibles auction software — AuctiSync"
      seoDescription="Collectibles auction software for sports cards, coins, comics, stamps. Grade-aware fields, PSA/BGS integration-ready, niche field templates."
      canonicalPath="/collectibles-auction-software"
      eyebrow="Niche"
      headline="For the hobbies that care about grade."
      subhead="Sports cards, coins, comics, stamps. Custom field templates for each category. Grade-aware sorting, PSA/BGS reference fields, multi-image front-and-back cataloging."
    >
      <Pillars
        title="Category-native cataloging"
        items={[
          {
            title: 'Sports cards',
            body: 'PSA/BGS grade field, player, year, set, card number, subset, parallel, serial, population.',
          },
          {
            title: 'Coins',
            body: 'Grade (PCGS/NGC/ANACS), year, mint mark, denomination, variety, strike, toning, population.',
          },
          {
            title: 'Comics',
            body: 'Grade (CGC/CBCS), title, issue, year, key issue flag, variant cover, signature series.',
          },
          {
            title: 'Stamps',
            body: 'Scott catalog number, grade, used/unused, centering, gum, hinging, cancellation.',
          },
          {
            title: 'Front + back photos',
            body: "Template requires both. Collectibles don't sell without it.",
          },
          {
            title: 'Watchlist + follow',
            body: 'Bidders follow sellers, save searches, get alerts when matching lots list.',
          },
        ]}
      />

      <CtaStrip
        headline="Catalog collectibles the way they actually trade."
        primaryLabel="See pricing"
        primaryHref="/pricing"
        secondaryLabel="Specialty segment"
        secondaryHref="/for-specialty"
      />
    </MarketingPage>
  );
}

export function StorageAuctionSoftware() {
  return (
    <MarketingPage
      seoTitle="Storage auction software — AuctiSync with state lien compliance"
      seoDescription="Self-storage auction software with state-specific lien compliance, certified notice delivery (RPost + USPS API), Column.us ad placement, and redemption tracking. 8% flat buyer premium."
      canonicalPath="/storage-auction-software"
      eyebrow="Niche"
      headline="From delinquent to sold — compliant in every state you operate."
      subhead="Self-storage auction software with the full delinquency-to-auction workflow built in. State-specific lien rules, certified notices, advertisement placement, redemption tracking — replaces StorageTreasures + Manage-by-Hand-Spreadsheets in one SKU."
    >
      <Pillars
        title="Full compliance engine, not just a listing site"
        items={[
          {
            title: '5 states at launch',
            body: "FL (explicit online-auction statute), TX (biggest market), AZ (OpenTech's home), GA, NC. ~30% of US facilities covered.",
          },
          {
            title: 'Certified notice delivery',
            body: 'RPost certified email + USPS Certified Mail API. Delivery proofs auto-attached to the audit PDF.',
          },
          {
            title: 'Column.us advertisement',
            body: "Required newspaper ads placed via Column's API. No manual calls to the local paper.",
          },
          {
            title: 'Redemption tracking',
            body: 'Tenant pays late? Auction auto-pauses. Clean void, no oversale.',
          },
          {
            title: 'Auto-compliance gate',
            body: "A listing literally can't publish until every state-required timer clears.",
          },
          {
            title: 'PMS integration included',
            body: 'Hummingbird, Easy Storage Solutions, Syrasoft, DomicoCloud, or CSV. One integration in every Storage-tier plan.',
          },
        ]}
      />

      <FeatureList
        title="What the Storage tier ($199/mo) includes"
        body="One SKU, delinquency-to-excess-proceeds."
        bullets={[
          '500 auction listings/year (overage at $0.50 each)',
          'All 5 launch states',
          'RPost + USPS Certified Mail APIs',
          'Column.us ad placement',
          'Send & Sign (unlimited storage-specific contracts)',
          'One PMS integration included',
          'Marketplace listing on LockerBids — 8% flat buyer premium',
          '4-year record retention (Texas standard)',
          '3 staff seats',
          'Basic reports + chat support',
        ]}
      />

      <CtaStrip
        headline="Stop managing lien calendars in a spreadsheet."
        subhead="Looking to bid on storage units? Visit LockerBids — our buyer-facing marketplace."
        primaryLabel="See Storage tier pricing"
        primaryHref="/pricing"
        secondaryLabel="See pricing"
        secondaryHref="/pricing"
      />
    </MarketingPage>
  );
}
